import * as TablerIcons from "vue-tabler-icons";
import { useSidebarItemStore } from "~/store/sidebar-item-store";

export interface menu {
  header?: string;
  title?: string;
  subroutes?: string[];
  icon?: any;
  to?: string;
  chip?: string;
  chipColor?: string;
  chipVariant?: string;
  chipIcon?: string;
  children?: menu[];
  disabled?: boolean;
  type?: string;
  subCaption?: string;
  roles?: string[];
  permissions?: string[];
}

interface RouteAccess {
  roles?: string[];
  permissions?: string[];
}

interface SidebarTreeNode {
  id?: string;
  title?: string;
  icon?: string;
  link?: string;
  children?: SidebarTreeNode[];
  [key: string]: unknown;
}

const baseStaticPageRoutes: menu[] = [
  {
    header: "Content",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"], //OR ["edit-content"]
  },
  {
    title: "Progressions",
    subroutes: ["/progressionsAddStep1", "/progressionMilestones"],
    icon: TablerIcons.AwardIcon,
    to: "/progressions",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"], //or ["edit-content"]
  },
  {
    title: "Reward Buckets",
    icon: TablerIcons.BucketIcon,
    to: "/buckets",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    title: "Dare Stats",
    icon: TablerIcons.ChecklistIcon,
    to: "/dares",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    title: "Contracts",
    icon: TablerIcons.FileTextIcon,
    to: "/contracts",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    title: "Tournaments",
    subroutes: ["/tournamentLocations"],
    icon: TablerIcons.TrophyIcon,
    to: "/tournaments",
    roles: ["admin"],
    permissions: ["edit-content"],
  },
  {
    title: "Leaderboards",
    icon: TablerIcons.CrownIcon,
    to: "/leaderboards",
    roles: ["admin"],
    permissions: ["edit-content"],
  },
  {
    title: "Game Contexts",
    icon: TablerIcons.DeviceGamepad2Icon,
    to: "/gamecontexts",
    roles: ["admin"],
    permissions: ["edit-content"],
  },
  {
    title: "Formulas",
    icon: TablerIcons.MathFunctionIcon,
    to: "/formulas",
    roles: ["admin"],
    permissions: ["edit-content"],
  },
  {
    title: "Location",
    icon: TablerIcons.LocationIcon,
    to: "/location",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-locations"],
  },
  {
    title: "Tags",
    icon: TablerIcons.TagIcon,
    to: "/tags",
    roles: ["admin", "support"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    header: "Store",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    title: "Store Items",
    icon: TablerIcons.DiscountCheckIcon,
    to: "/store",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    title: "Organize Store",
    icon: TablerIcons.LayoutGridAddIcon,
    to: "/store_organize",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    title: "Image Types",
    icon: TablerIcons.PhotoIcon,
    to: "/imageTypes",
    roles: ["admin"], // Only admin can see
    permissions: ["edit-content"],
  },
  {
    title: "Store Transactions",
    icon: TablerIcons.ReceiptIcon,
    to: "/storeTransactions",
    roles: ["admin"],
    permissions: ["edit-content"],
  },
  {
    header: "Content Management",
    roles: ["admin"], // Only admin can see
  },
  {
    title: "Savepoints",
    icon: TablerIcons.DiamondIcon,
    to: "/savepoints",
    roles: ["admin"], // Only admin can see
    permissions: ["migrations"],
  },
  {
    title: "Migrations",
    icon: TablerIcons.HandMoveIcon,
    to: "/migrations",
    roles: ["admin"], // Only admin can see
    permissions: ["migrations"],
  },
  {
    title: "Content Types",
    icon: TablerIcons.PackageIcon,
    to: "/content-types",
    roles: ["admin"], // Only admin can access
  },
  {
    title: "Content Items",
    icon: TablerIcons.PackagesIcon,
    to: "/content-items",
    roles: ["admin"], // Only admin can access
  },
  {
    header: "Support",
    roles: ["admin", "support"],
    permissions: ["edit-players"],
  },
  {
    title: "Players List",
    icon: TablerIcons.DeviceGamepad2Icon,
    to: "/profile",
    roles: ["admin", "support"],
    permissions: ["edit-players"],
  },
  {
    title: "Moderation Review",
    icon: TablerIcons.ShieldIcon,
    to: "/admin/moderationReview",
    roles: ["admin", "support"],
  },
  {
    title: "Grants",
    icon: TablerIcons.GiftIcon,
    to: "/adhoc",
    roles: ["admin", "support"], // Only admin can see
  },
  {
    title: "Error Logs",
    icon: TablerIcons.ReportAnalyticsIcon,
    to: "/logs",
    roles: ["admin", "support"], // Only admin can see
  },
  {
    title: "Scheduled jobs",
    icon: TablerIcons.ClockIcon,
    to: "/scheduledJobs",
    roles: ["admin"],
    permissions: ["edit-content"],
  },
  { header: "System", roles: ["admin"] },
  {
    title: "Projects",
    icon: TablerIcons.FoldersIcon,
    to: "/admin/projects",
    roles: ["admin"],
  },
  {
    title: "Server Environments",
    icon: TablerIcons.ServerIcon,
    to: "/admin/serverEnvironments",
    roles: ["admin"],
  },
  {
    title: "Servers",
    icon: TablerIcons.ServerCogIcon,
    to: "/servers",
    roles: ["admin"],
  },
  { header: "Access Management", roles: ["admin"] },
  {
    title: "Users",
    icon: TablerIcons.UserIcon,
    to: "/admin/users",
    roles: ["admin"],
  },
  {
    title: "Roles",
    icon: TablerIcons.AtIcon,
    to: "/admin/roles",
    roles: ["admin"],
  },
  {
    title: "Permissions",
    icon: TablerIcons.LockAccessIcon,
    to: "/admin/permissions",
    roles: ["admin"],
  },
  {
    title: "Sidebar Items",
    icon: TablerIcons.LayoutSidebarLeftCollapseIcon,
    to: "/admin/sidebarItem",
    roles: ["admin"],
  },
];

export function groupContentRoutes(routes: menu[]): menu[] {
  const groups = [
    { header: "General", paths: ["/dares", "/buckets", "/tags", "/formulas"] },
    { header: "Locations", paths: ["/location"] },
    {
      header: "Game Content",
      paths: [
        "/progressions",
        "/contracts",
        "/tournaments",
        "/leaderboards",
        "/gamecontexts",
      ],
    },
    { header: "JSON Content", paths: ["/content-types", "/content-items"] },
  ];
  const path = (route: menu) =>
    (route.to || "").replace(/\/+$/, "").toLowerCase();
  const contentPaths = new Set(groups.flatMap((group) => group.paths));
  const firstContentIndex = routes.findIndex((route) =>
    contentPaths.has(path(route)),
  );
  if (firstContentIndex < 0) return routes;
  let insertionIndex = firstContentIndex;
  for (let index = firstContentIndex - 1; index >= 0; index--) {
    if (routes[index].header) {
      insertionIndex = index;
      break;
    }
  }

  const grouped: menu[] = groups.flatMap((group) => {
    const items = group.paths.flatMap((itemPath) =>
      routes.filter((route) => path(route) === itemPath),
    );
    return items.length ? [{ header: group.header }, ...items] : [];
  });
  const result: menu[] = [];
  routes.forEach((route, index) => {
    if (index === insertionIndex) result.push(...grouped);
    if (!contentPaths.has(path(route))) result.push(route);
  });
  // Drop section headings whose items have all moved to the new content groups.
  return result.filter(
    (route, index) =>
      !route.header || Boolean(result[index + 1] && !result[index + 1].header),
  );
}

export const staticPageRoutes = groupContentRoutes(baseStaticPageRoutes);

const inferPermissionFromTitle = (route: menu): string[] | undefined => {
  if (!route.title || !route.to || route.to === "/") {
    return undefined;
  }

  const normalizedTitle = route.title
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");

  if (!normalizedTitle) {
    return undefined;
  }

  return [`edit-${normalizedTitle}`];
};

const normalizeRoutePath = (path?: string | null): string => {
  return path ? path.replace(/\/+$/, "").toLowerCase() : "";
};

const findRouteByPath = (
  routes: menu[],
  normalizedPath: string,
): menu | undefined => {
  for (const route of routes || []) {
    if (normalizeRoutePath(route.to) === normalizedPath) {
      return route;
    }

    if (
      Array.isArray(route.subroutes) &&
      route.subroutes.some(
        (subroute) => normalizeRoutePath(subroute) === normalizedPath,
      )
    ) {
      return route;
    }

    if (Array.isArray(route.children) && route.children.length > 0) {
      const match = findRouteByPath(route.children, normalizedPath);
      if (match) {
        return match;
      }
    }
  }

  return undefined;
};

const resolveRouteAccessForPath = (
  path?: string | null,
): RouteAccess | undefined => {
  const normalizedPath = normalizeRoutePath(path);

  if (!normalizedPath) {
    return undefined;
  }

  const matchedRoute =
    findRouteByPath(pageRoutes, normalizedPath) ||
    findRouteByPath(staticPageRoutes, normalizedPath);

  if (!matchedRoute) {
    return undefined;
  }

  const inferredPermissions =
    matchedRoute.permissions && matchedRoute.permissions.length > 0
      ? undefined
      : inferPermissionFromTitle(matchedRoute);

  return {
    roles:
      matchedRoute.roles && matchedRoute.roles.length > 0
        ? matchedRoute.roles
        : undefined,
    permissions:
      inferredPermissions && inferredPermissions.length > 0
        ? inferredPermissions
        : matchedRoute.permissions && matchedRoute.permissions.length > 0
          ? matchedRoute.permissions
          : undefined,
  };
};

const resolveEffectiveRouteAccess = (route: menu): RouteAccess => {
  const inferredPermissions =
    route.permissions && route.permissions.length > 0
      ? undefined
      : inferPermissionFromTitle(route);

  const fallback = resolveRouteAccessForPath(route.to);

  return {
    roles:
      route.roles && route.roles.length > 0 ? route.roles : fallback?.roles,
    permissions:
      inferredPermissions && inferredPermissions.length > 0
        ? inferredPermissions
        : route.permissions && route.permissions.length > 0
          ? route.permissions
          : fallback?.permissions,
  };
};

export const getEffectiveRouteAccess = (
  route?: menu | null,
): RouteAccess | null => {
  if (!route) {
    return null;
  }

  return resolveEffectiveRouteAccess(route);
};

export const getRouteAccessForPath = (
  path?: string | null,
): RouteAccess | null => {
  const normalizedPath = normalizeRoutePath(path);

  if (!normalizedPath) {
    return null;
  }

  let matchedRoute = findRouteByPath(pageRoutes, normalizedPath);

  if (!matchedRoute) {
    matchedRoute = findRouteByPath(staticPageRoutes, normalizedPath);
  }

  if (matchedRoute) {
    return resolveEffectiveRouteAccess(matchedRoute);
  }

  return null;
};

const withFallbackAccess = (
  link: string | null | undefined,
  roles?: string[],
  permissions?: string[],
): RouteAccess => {
  const fallback = resolveRouteAccessForPath(link);

  return {
    roles: roles && roles.length > 0 ? roles : fallback?.roles,
    permissions:
      permissions && permissions.length > 0
        ? permissions
        : fallback?.permissions,
  };
};

const getSidebarIcon = (icon?: string | null) => {
  if (!icon) {
    return undefined;
  }

  const iconName = icon.trim();

  if (!iconName) {
    return undefined;
  }

  return (TablerIcons as Record<string, any>)[iconName] || undefined;
};

const toStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    const normalized = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter((entry) => entry.length > 0);

    return normalized.length > 0 ? normalized : undefined;
  }

  if (typeof value === "string") {
    const normalized = value
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    return normalized.length > 0 ? normalized : undefined;
  }

  return undefined;
};

const resolveRoles = (sidebarItem: SidebarTreeNode): string[] | undefined => {
  const untypedItem = sidebarItem as Record<string, unknown>;

  return (
    toStringArray(untypedItem.roles) ||
    toStringArray(untypedItem.Roles) ||
    toStringArray(untypedItem.roleNames) ||
    toStringArray(untypedItem.RoleNames) ||
    toStringArray(untypedItem.allowedRoles) ||
    toStringArray(untypedItem.AllowedRoles) ||
    toStringArray(untypedItem.requiredRoles) ||
    toStringArray(untypedItem.RequiredRoles)
  );
};

const resolvePermissions = (
  sidebarItem: SidebarTreeNode,
): string[] | undefined => {
  const untypedItem = sidebarItem as Record<string, unknown>;

  return (
    toStringArray(untypedItem.permissions) ||
    toStringArray(untypedItem.Permissions) ||
    toStringArray(untypedItem.permissionNames) ||
    toStringArray(untypedItem.PermissionNames) ||
    toStringArray(untypedItem.allowedPermissions) ||
    toStringArray(untypedItem.AllowedPermissions) ||
    toStringArray(untypedItem.requiredPermissions) ||
    toStringArray(untypedItem.RequiredPermissions)
  );
};

const buildMenuItem = (sidebarItem: SidebarTreeNode): menu => {
  const children = (sidebarItem.children || [])
    .map((child: SidebarTreeNode) => buildMenuItem(child))
    .filter((child) => child.title || child.header);

  const roles = resolveRoles(sidebarItem);
  const permissions = resolvePermissions(sidebarItem);
  const fallbackAccess = withFallbackAccess(
    sidebarItem.link,
    roles,
    permissions,
  );

  if (!sidebarItem.link) {
    return {
      header: sidebarItem.title || "",
      children: children.length > 0 ? children : undefined,
      roles: fallbackAccess.roles,
      permissions: fallbackAccess.permissions,
    };
  }

  return {
    title: sidebarItem.title || "",
    icon: getSidebarIcon(sidebarItem.icon),
    to: sidebarItem.link || "",
    children: children.length > 0 ? children : undefined,
    roles: fallbackAccess.roles,
    permissions: fallbackAccess.permissions,
  };
};

export const buildSidebarMenuFromTree = (
  sidebarTree: SidebarTreeNode[],
): menu[] => {
  const builtMenu: menu[] = [];

  for (const sidebarItem of sidebarTree || []) {
    const menuItem = buildMenuItem(sidebarItem);

    if (menuItem.header && menuItem.children?.length) {
      builtMenu.push({
        header: menuItem.header,
      });

      builtMenu.push(...menuItem.children);
      continue;
    }

    if (menuItem.header || menuItem.title) {
      builtMenu.push(menuItem);
    }
  }

  return groupContentRoutes(builtMenu);
};

export const fallbackPageRoutes: menu[] = [
  { title: "Dashboard", icon: TablerIcons.HomeIcon, to: "/" },
];

export const withAdminManagementRoutes = (routes: menu[]): menu[] => {
  const result = [...routes];
  let section: menu | undefined;
  let changed = false;
  for (const route of staticPageRoutes) {
    if (route.header) { section = route; continue; }
    if (!route.to || findRouteByPath(result, normalizeRoutePath(route.to))) continue;
    changed = true;
    if (!section) { result.unshift(route); continue; }
    let sectionIndex = result.findIndex((item) => item.header === section!.header);
    if (sectionIndex < 0) {
      result.push(section);
      sectionIndex = result.length - 1;
    }
    const nextSection = result.findIndex((item, index) => index > sectionIndex && Boolean(item.header));
    result.splice(nextSection < 0 ? result.length : nextSection, 0, route);
  }
  return changed ? result : routes;
};

export const pageRoutes: menu[] = [...fallbackPageRoutes];

export const setPageRoutes = (routes: menu[]) => {
  pageRoutes.splice(0, pageRoutes.length, ...routes);
};

export const resetPageRoutesToFallback = () => {
  setPageRoutes(fallbackPageRoutes);
};

export const loadSidebarItemsIntoPageRoutes = async () => {
  const sidebarItemStore = useSidebarItemStore();

  await sidebarItemStore.fetchSidebarTree();

  const dbRoutes = buildSidebarMenuFromTree(sidebarItemStore.sidebarTree || []);

  const routesWithoutDashboard = dbRoutes.filter(
    (route) => normalizeRoutePath(route.to) !== "/",
  );

  if (dbRoutes.length > 0) {
    setPageRoutes([...fallbackPageRoutes, ...routesWithoutDashboard]);
    return pageRoutes;
  }

  setPageRoutes(staticPageRoutes);
  return pageRoutes;
};

const getAccessName = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;

    if (typeof record.name === "string") {
      return record.name;
    }

    if (typeof record.Name === "string") {
      return record.Name;
    }
  }

  return "";
};

const normalizeAccessName = (value: string): string => {
  return value.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, " ");
};

const normalizePermissionBase = (value: string): string => {
  let normalized = normalizeAccessName(value);

  const colonIndex = normalized.indexOf(":");

  if (colonIndex >= 0) {
    normalized = normalized.substring(0, colonIndex).trim();
  }

  if (normalized === "migrations") {
    normalized = "migration";
  }

  return normalized;
};

const normalizePermissionScope = (value: string): string => {
  const normalized = normalizeAccessName(value);

  const colonIndex = normalized.indexOf(":");

  if (colonIndex < 0) {
    return "";
  }

  return normalized
    .substring(colonIndex + 1)
    .trim()
    .replace(/\s+/g, "-");
};

export function hasPermission(
  requiredPermissions: string[],
  userPermissions: unknown[],
): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  return requiredPermissions.some((requiredPermission) => {
    const requiredBase = normalizePermissionBase(requiredPermission);
    const requiredScope = normalizePermissionScope(requiredPermission);

    if (!requiredBase) {
      return false;
    }

    return userPermissions.some((userPermission) => {
      const userPermissionName = getAccessName(userPermission);

      if (!userPermissionName) {
        return false;
      }

      const userBase = normalizePermissionBase(userPermissionName);
      const userScope = normalizePermissionScope(userPermissionName);

      const baseMatches =
        userBase === requiredBase ||
        (!requiredScope && userBase.startsWith(requiredBase + "-"));

      if (!baseMatches) {
        return false;
      }

      if (!requiredScope) {
        return true;
      }

      return userScope === requiredScope;
    });
  });
}

export function hasRole(
  requiredRoles: string[],
  userRoles: unknown[],
): boolean {
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }

  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  return requiredRoles.some((requiredRole) => {
    const normalizedRequiredRole = normalizeAccessName(requiredRole);

    return userRoles.some((userRole) => {
      const userRoleName = getAccessName(userRole);

      if (!userRoleName) {
        return false;
      }

      const normalizedUserRole = normalizeAccessName(userRoleName);

      return (
        normalizedUserRole === normalizedRequiredRole ||
        normalizedUserRole.startsWith(normalizedRequiredRole + "-")
      );
    });
  });
}

export default pageRoutes;
